import ApiCall from "../Apicall/ApiCall";
import i18n from "i18next";
import { showDealExpiredDialog, showSellerCancelledDialog } from "../public/components/ds/dealDialogs";

/**
 * If the user has a deal that expired before being finished and they have not yet been
 * told, tell them once — "The deal is expired" — and let them go to the home page. The deal's
 * locally cached data is cleared. Returns true when the notice was shown (caller must not
 * route further).
 */
export async function showExpiredDealNoticeIfAny(navigate) {
  try {
    const res = await ApiCall({ url: "/ContractMain/GetExpiredDealNotice", method: "GET" });
    const deal = res?.result || res?.data?.result;
    if (!deal?.id) return false;

    if (deal.dealState === "cancelled") await showSellerCancelledDialog((k) => i18n.t(k), deal);
    else await showDealExpiredDialog((k) => i18n.t(k), deal);

    localStorage.removeItem("currentContractID");
    localStorage.removeItem("data");
    try {
      await ApiCall({ url: "/ContractMain/AcknowledgeExpiredDeal", method: "POST", data: { id: deal.id } });
    } catch (_) {
      /* worst case the notice shows once more */
    }
    // the signed-in home page ("/Home" is the public landing page and looks logged out)
    navigate("/user/ChooseAction", { replace: true });
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * True when the user still needs to complete their profile.
 *  - A BankID-verified account fills in the same form as an email sign-up, except that the
 *    e-mail is optional: it is complete once it has its (BankID) name and a phone number.
 *  - Otherwise (email sign-up) the core fields are required: first name, surname, email, phone.
 *  - Email signups seed name/surname with the email address (placeholder) → incomplete.
 *  - (Older servers) BankID signups seed bankDetail with the personnummer (placeholder) until
 *    the user enters a real bank account → treat that as first-time incomplete.
 */
export function isProfileIncomplete(profile) {
  if (!profile) return true;

  const isBlank = (v) => !v || String(v).trim() === "";
  const { name, surname, emailAddress, personalContact, bankDetail, bankIdSSN } = profile;

  const hasRealName = !isBlank(name) && !isBlank(surname) && !`${name}${surname}`.includes("@");
  if (profile.isBankIdVerified === true && hasRealName) return isBlank(personalContact);

  // core required fields must be present
  if ([name, surname, emailAddress, personalContact].some(isBlank)) return true;

  // email placeholder still sitting in the name fields
  if (emailAddress && (name === emailAddress || surname === emailAddress)) return true;

  // fresh BankID user: bankDetail still holds the SSN placeholder (no real bank account yet)
  if (bankIdSSN && bankDetail && bankDetail === bankIdSSN) return true;

  return false;
}

/**
 * After login: check if user has an active contract and redirect accordingly.
 *   - Profile not completed       → /CompleteProfile
 *   - Seller / connected buyer with an active contract → /user/ChooseAction (the overview shows the
 *     ongoing deal; the user resumes it from there)
 *   - Buyer with an active contract, not yet connected → /BuyerSuccess
 *   - No active contract          → /user/ChooseAction
 */
// Where a connected buyer resumes: once the seller has confirmed the buyer the pair is on
// the contract review/sign step (4); before that, the waiting step (2).
export const buyerResumeState = (contract) => ({
  startStep: contract.sellerDealComplete ? 4 : 2,
  inviteContractId: contract.id,
});

export async function postLoginRedirect(navigate) {
  try {
    // 1. Get current user id
    const sessionRes = await ApiCall({
      url: "/Session/GetCurrentLoginInformations",
      method: "GET",
    });
    const userId = sessionRes?.data?.result?.user?.id;

    if (!userId) {
      navigate("/user/ChooseAction", { replace: true });
      return;
    }

    // 1b. Gate on profile completeness before any other routing.
    try {
      const profRes = await ApiCall({
        url: "/User/GetUserProfileById",
        method: "GET",
        params: { Id: userId },
      });
      const profile = profRes?.data?.result;
      if (isProfileIncomplete(profile)) {
        navigate("/CompleteProfile", { replace: true });
        return;
      }
    } catch (_) {
      // if the completeness check itself fails, fall through to normal routing
    }

    // 2. Check for active contract
    const contractRes = await ApiCall({
      url: "/ContractMain/GetActiveContractByUserId",
      method: "GET",
      params: { Id: userId },
    });

    const contract = contractRes?.result || contractRes?.data?.result;

    if (contract && contract.id) {
      // Store contract ID for the profile pages
      localStorage.setItem("currentContractID", contract.id);

      if (contract.sellerUserId === userId || contract.buyerDealConnected) {
        // The seller, or a buyer already connected to the deal, lands on the overview, which
        // shows the ongoing deal. Resuming it (View deal / Continue there) is what takes them
        // back to the step they left — see ChooseAction.resumeDeal / buyerResumeState.
        navigate("/user/ChooseAction", { replace: true });
      } else {
        // BuyerSuccess reads from "data" key — set it so the page can load the contract
        localStorage.setItem("data", JSON.stringify({
          contractId: contract.id,
          sellerUserId: contract.sellerUserId,
        }));
        navigate("/BuyerSuccess", { replace: true });
      }
    } else {
      if (await showExpiredDealNoticeIfAny(navigate)) return;
      navigate("/user/ChooseAction", { replace: true });
    }
  } catch (_) {
    // If any error (e.g. no contract found), fall back to ChooseAction
    navigate("/user/ChooseAction", { replace: true });
  }
}

/**
 * For the buyer-specific login page: if the buyer already has a deal they were
 * connected to, resume it on the current BuyerProfile waiting screen. Returns
 * true when it navigated, false when the caller should carry on as before.
 */
export async function resumeConnectedBuyerDeal(navigate) {
  try {
    const sessionRes = await ApiCall({ url: "/Session/GetCurrentLoginInformations", method: "GET" });
    const userId = sessionRes?.data?.result?.user?.id;
    if (!userId) return false;

    const contractRes = await ApiCall({
      url: "/ContractMain/GetActiveContractByUserId",
      method: "GET",
      params: { Id: userId },
    });
    const contract = contractRes?.result || contractRes?.data?.result;
    if (!contract?.id) return await showExpiredDealNoticeIfAny(navigate);
    if (contract.sellerUserId === userId || !contract.buyerDealConnected) return false;

    // Lands on the overview, which shows the ongoing deal; resuming it is done from there.
    localStorage.setItem("currentContractID", contract.id);
    navigate("/user/ChooseAction", { replace: true });
    return true;
  } catch (_) {
    return false;
  }
}
